import { Skeleton } from "@mantine/core";
import { Link } from "@tanstack/react-router";
import { Ban, Package } from "lucide-react";

import { useListProducts } from "../../../api/generated/endpoints/teams/teams";
import type { Product } from "../../../api/generated/model";
import { freshUrls, soonestExpiry } from "../../../api/signed-urls";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import type { ProductChoice } from "../../../platform/media/ProductPicker";
import { ThumbImage } from "../../../ui/ThumbImage";
import { coverOf } from "../products/cover";
import classes from "./simple.module.css";

/** The product's photo the composer takes: the card's cover, the first product shot else image. */
function choiceOf(product: Product): ProductChoice | null {
  const lead = product.images.find((image) => image.tag === "product_shot") ?? product.images[0];
  if (lead === undefined) return null;
  return { productId: product.id, assetId: lead.asset_id, url: lead.thumb_url ?? lead.url, name: product.name };
}

interface ProductStepProps {
  teamId: string;
  brandId: string;
  chosen: string | null;
  onChoose: (product: ProductChoice | null) => void;
}

/** Step 2, optional: "No product" first, then the brand's products with a photo to start from. */
export function ProductStep({ teamId, brandId, chosen, onChoose }: ProductStepProps) {
  // The covers' signed URLs expire, so the list is read again before they do (the Products page's).
  const products = useListProducts(teamId, brandId, undefined, {
    query: freshUrls((list: Product[]) => soonestExpiry(list.map((each) => each.urls_expire_at))),
  });
  return (
    <section className={classes.step} aria-labelledby="simple-product">
      <h2 id="simple-product" className={classes.stepTitle}>
        For which product? <span className={classes.optional}>Optional</span>
      </h2>
      {products.isError ? <ErrorAlert error={products.error} /> : null}
      {products.data === undefined ? (
        products.isError ? null : (
          <div className={classes.products} aria-hidden>
            {[0, 1, 2, 3].map((each) => (
              <Skeleton key={each} className={classes.productSkeleton} />
            ))}
          </div>
        )
      ) : (
        <ul className={classes.products} aria-label="Products">
          <li>
            <button
              type="button"
              className={classes.product}
              aria-pressed={chosen === null}
              onClick={() => onChoose(null)}
            >
              <span className={classes.productPicture}>
                <Ban size={22} strokeWidth={1.5} aria-hidden />
              </span>
              <span className={classes.productName}>No product</span>
            </button>
          </li>
          {products.data.map((product) => {
            const choice = choiceOf(product);
            const cover = coverOf(product);
            return (
              <li key={product.id}>
                <button
                  type="button"
                  className={classes.product}
                  aria-pressed={chosen === product.id}
                  // A product with no photo has nothing to start from.
                  disabled={choice === null}
                  title={choice === null ? "This product has no photo yet" : undefined}
                  onClick={() => onChoose(choice)}
                >
                  <span className={classes.productPicture}>
                    {cover === null ? (
                      <Package size={22} strokeWidth={1.5} aria-hidden />
                    ) : (
                      <ThumbImage thumb={cover.thumb} full={cover.full} alt="" onLost={() => void products.refetch()} />
                    )}
                  </span>
                  <span className={classes.productName}>{product.name}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {products.data?.length === 0 ? (
        <p className={classes.muted}>
          This brand has no products yet.{" "}
          <Link to="/teams/$teamId/brands/$brandId/pro/products" params={{ teamId, brandId }}>
            Add one in Products
          </Link>
          .
        </p>
      ) : null}
    </section>
  );
}
